import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useCatalog } from '@/features/quotes/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input, Label } from '@/components/ui/input';
import { useUpdatePricingRules } from './api';

export function QuoteDefaultsSettings({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation('settings');
  const { data: catalog } = useCatalog(workspaceId);
  const updateRules = useUpdatePricingRules(workspaceId);
  const rules = catalog?.pricingRules;
  if (!rules) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('quoteDefaults.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>{t('quoteDefaults.depositPct')}</Label>
            <Input
              type="number"
              min={0}
              max={100}
              defaultValue={rules.depositPct}
              onBlur={(e) => Number(e.target.value) !== rules.depositPct && updateRules.mutate({ depositPct: Number(e.target.value) })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('quoteDefaults.validityDays')}</Label>
            <Input
              type="number"
              min={1}
              defaultValue={rules.quoteValidityDays}
              onBlur={(e) => Number(e.target.value) !== rules.quoteValidityDays && updateRules.mutate({ quoteValidityDays: Number(e.target.value) })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('quoteDefaults.tax')}</Label>
            <Input
              type="number"
              min={0}
              max={30}
              step="0.01"
              defaultValue={(rules.taxRateBps / 100).toFixed(2)}
              key={rules.taxRateBps}
              onBlur={(e) => {
                const bps = Math.round(Number(e.target.value) * 100);
                if (bps !== rules.taxRateBps) updateRules.mutate({ taxRateBps: bps });
              }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t('quoteDefaults.terms')}</Label>
          <textarea
            defaultValue={rules.termsText}
            rows={6}
            onBlur={(e) => {
              if (e.target.value !== rules.termsText) {
                updateRules.mutate({ termsText: e.target.value });
                toast.success(t('toast.termsSaved'));
              }
            }}
            className="rounded-[var(--radius-input)] border border-border bg-surface px-3 py-2 text-sm outline-none focus-visible:border-accent"
          />
        </div>
      </CardContent>
    </Card>
  );
}
